import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

export interface Column<T> {
  header: string;
  accessor: (row: T) => ReactNode;
  className?: string;
  /** Classes aplicadas ao <th> (ex.: largura `w-[20%]`, alinhamento). */
  headerClassName?: string;
}

interface Props<T> {
  columns: Column<T>[];
  data: T[];
  emptyState?: ReactNode;
  rowKey: (row: T) => string;
  /** Linha clicável (cursor + onClick). Botões internos devem dar stopPropagation. */
  onRowClick?: (row: T) => void;
}

export function DataTable<T>({ columns, data, emptyState, rowKey, onRowClick }: Props<T>) {
  if (data.length === 0) {
    return (
      <div className="animate-fade-in rounded-lg border border-dashed bg-surface/40 p-8 text-center text-sm text-muted-foreground">
        {emptyState ?? "Nenhum item encontrado."}
      </div>
    );
  }
  return (
    <div className="overflow-x-auto rounded-lg border bg-card">
      <table className="w-full text-sm">
        <thead className="border-b bg-muted/40">
          <tr>
            {columns.map((col) => (
              <th
                key={col.header}
                className={cn(
                  "px-4 py-3 text-left font-medium text-muted-foreground",
                  col.headerClassName
                )}
              >
                {col.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((row) => (
            <tr
              key={rowKey(row)}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              className={cn(
                "border-b last:border-0 hover:bg-muted/20",
                onRowClick && "cursor-pointer"
              )}
            >
              {columns.map((col, idx) => (
                <td key={idx} className={cn("px-4 py-3", col.className)}>
                  {col.accessor(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
